import { formatZeros } from './formatZeros'
import { OVERLORD_LEVY_RATE, OVERLORD_VISIT_PROBABILITY } from './overlordLevy'

/**
 * The Boss Invader — every number the boss system uses, in one pure module
 * (the `overlordLevy.ts` pattern), shared by the engine, the `attackBoss`
 * player function, the frontend attack UI and the simulator so none of them
 * can drift.
 *
 * The boss is the Galactic Overlords' replacement: in a game with
 * `boss_invaders_enabled` the overlords never visit, and the boss becomes the
 * game's money sink instead — a constant, inflation-matched drain that lets
 * everything else keep an always-positive return. Its numbers therefore hang
 * off the same two scalars the overlords use: `relativeResourceQuantities`
 * (calendar growth) and the Au mine price (the economy's price level).
 */

/**
 * Levy rate per year ALIVE. The overlords take `rrq * 1.5 * au / sqrt(seats)`
 * in 25% of years; the boss levies every year it is alive, so a level-1 boss
 * at `1.5 * 0.25` drains exactly the overlords' expected annual amount.
 */
export const BOSS_LEVY_RATE = OVERLORD_LEVY_RATE * OVERLORD_VISIT_PROBABILITY

/**
 * Each boss level past 1 adds 15% to the levy (and the kill reward) — the
 * escalation that makes ignoring the boss strictly worse than the overlords
 * ever were. Balance knob.
 */
export const BOSS_LEVY_LEVEL_GROWTH = 0.15

/**
 * What every living player pays each year the boss is alive. Same shape as
 * `overlordLevy`: split by the SQUARE ROOT of the seat count (dead seats
 * included), `jitter` uniform on [0.75, 1.25) averaging 1.
 */
export function bossLevy(
  relativeResourceQuantities: number,
  auMineCash: number,
  seatCount: number,
  level: number,
  jitter = 1,
): number {
  return Math.round(
    relativeResourceQuantities * BOSS_LEVY_RATE * (1 + BOSS_LEVY_LEVEL_GROWTH * (level - 1))
    * auMineCash / Math.sqrt(Math.max(seatCount, 1)) * jitter,
  )
}

/**
 * Player missiles. Damage per missile rolls 1..max; max grows 20% per weapons
 * level (the owner's spec) on its own curve — deliberately steeper than the
 * PvP die (`die_sides = level * 2 + 4`) so weapons upgrades stay meaningful
 * against a boss whose menace compounds yearly.
 */
export const BOSS_MISSILE_BASE_MAX_DAMAGE = 10
export const BOSS_MISSILE_DAMAGE_GROWTH = 1.2

export function bossMissileMaxDamage(weaponsLevel: number): number {
  return Math.round(BOSS_MISSILE_BASE_MAX_DAMAGE * BOSS_MISSILE_DAMAGE_GROWTH ** (Math.max(weaponsLevel, 1) - 1))
}

/**
 * Uranium per missile: 1 U through level 5, 2 U from level 6, 3 U from
 * level 13. Damage per MISSILE always improves with weapons level; damage
 * per U improves within each cost tier and in the long run, with a brief dip
 * where the integer cost steps up — acceptable, since the scarce resource is
 * really the once-per-turn attack window.
 */
export const BOSS_MISSILE_U_BASE_COST = 1
export const BOSS_MISSILE_U_COST_GROWTH = 1.1

export function bossMissileUCost(weaponsLevel: number): number {
  return Math.max(1, Math.round(BOSS_MISSILE_U_BASE_COST * BOSS_MISSILE_U_COST_GROWTH ** (Math.max(weaponsLevel, 1) - 1)))
}

/**
 * Total damage of a volley of player missiles: each rolls `ceil(rand * max)`,
 * the same die convention as the PvP battle.
 */
export function bossAttackDamage(missiles: number, weaponsLevel: number, random: () => number = Math.random): number {
  const max = bossMissileMaxDamage(weaponsLevel)
  let total = 0
  for (let i = 0; i < missiles; i++) {
    total += Math.ceil(random() * max)
  }
  return total
}

/**
 * The weapons level a table is EXPECTED to have around a given year — the
 * owner's pacing intent of "players upgrade weapons every other year from
 * year 40". Only used to size boss health at spawn.
 */
export function expectedWeaponsLevelAtTurn(turn: number): number {
  return Math.max(1, Math.round((turn - 40) / 2) + 1)
}

/**
 * Boss health, fixed at spawn (levels raise menace, not durability — a
 * neglected boss stays killable, just ever more expensive to ignore).
 * `BOSS_TURNS_TO_KILL` is the sizing intent: how long the whole table firing
 * an ordinary volley needs to bring it down. Cut to a TENTH of the original
 * 3.5 (owner call, 2026-08-05): uranium is expensive enough that the old
 * hull outlasted the table's ability to pay for ammunition — a coordinated
 * table now downs the boss inside a year. Balance knobs.
 */
export const BOSS_TURNS_TO_KILL = 0.35
export const BOSS_EXPECTED_MISSILES_PER_ATTACK = 3

export function bossMaxHealth(spawnTurn: number, alivePlayerCount: number): number {
  const expectedMax = bossMissileMaxDamage(expectedWeaponsLevelAtTurn(spawnTurn))
  return Math.round(
    BOSS_TURNS_TO_KILL * Math.max(alivePlayerCount, 1) * BOSS_EXPECTED_MISSILES_PER_ATTACK * (expectedMax + 1) / 2,
  )
}

/**
 * The boss's yearly bombardment budget, in STRENGTH points, split evenly
 * across every player-owned planet in its quadrant (more planets = thinner
 * spread — extra planets are never extra punishment).
 *
 * Planet strength does not inflate 10.5%/yr like prices do, so the Au price
 * enters through a fourth root: enough to keep a late-game boss relevant
 * against Pt-pumped defences, without one-shotting whole quadrants. Level is
 * the primary growth axis. Balance knob: `BOSS_VOLLEY_BASE`.
 */
export const BOSS_VOLLEY_BASE = 12
export const BOSS_VOLLEY_LEVEL_GROWTH = 1.2
export const BOSS_VOLLEY_PRICE_EXPONENT = 0.25
export const AU_MINE_BASE_PRICE = 1500

export function bossVolleyBudget(level: number, auMineCash: number): number {
  const priceFactor = (Math.max(auMineCash, AU_MINE_BASE_PRICE) / AU_MINE_BASE_PRICE) ** BOSS_VOLLEY_PRICE_EXPONENT
  return formatZeros(BOSS_VOLLEY_BASE * BOSS_VOLLEY_LEVEL_GROWTH ** (Math.max(level, 1) - 1) * priceFactor, 2)
}

export function bossVolleyPerPlanet(budget: number, targetCount: number): number {
  return formatZeros(budget / Math.max(targetCount, 1), 2)
}

/**
 * Kill reward pool, split by damage share. Roughly two-thirds of what a boss
 * killed after ~4 years drained from the table — killing pays, but the galaxy
 * stays net poorer (the boss remains a money SINK). Balance knob.
 */
export const BOSS_KILL_REWARD_RATE = 1.0

export function bossKillReward(
  relativeResourceQuantities: number,
  auMineCash: number,
  seatCount: number,
  level: number,
): number {
  return Math.round(
    relativeResourceQuantities * auMineCash * Math.sqrt(Math.max(seatCount, 1)) * BOSS_KILL_REWARD_RATE
    * (1 + BOSS_LEVY_LEVEL_GROWTH * (level - 1)),
  )
}

/** Proportional split of the reward pool; rounded per share, zero-damage contributors get nothing. */
export function splitBossReward(pool: number, damageByPlayer: { [uid: string]: number }): { [uid: string]: number } {
  const total = Object.values(damageByPlayer).reduce((sum, damage) => sum + Math.max(damage, 0), 0)
  const shares: { [uid: string]: number } = {}
  if (total <= 0 || pool <= 0) {
    return shares
  }
  for (const [uid, damage] of Object.entries(damageByPlayer)) {
    if (damage > 0) {
      shares[uid] = Math.round(pool * damage / total)
    }
  }
  return shares
}

/** The primary quadrant a planet key belongs to: `'S|β:Stargate|28'` → `'S'`, `'E:Earth'` → `'E'`. */
export function quadrantOfPlanetKey(key: string): string {
  return key.split(':')[0].split('|')[0]
}

/**
 * Spawn weighting: each quadrant weighs the number of DISTINCT living players
 * owning at least one planet there — the boss goes where the players are.
 */
export function bossQuadrantWeights(
  players: ReadonlyArray<{ planets: readonly string[]; dead?: boolean }>,
): { [quadrant: string]: number } {
  const weights: { [quadrant: string]: number } = {}
  for (const player of players) {
    if (player.dead) {
      continue
    }
    const quadrants = new Set(player.planets.map(quadrantOfPlanetKey))
    for (const quadrant of quadrants) {
      weights[quadrant] = (weights[quadrant] || 0) + 1
    }
  }
  return weights
}

/**
 * Weighted quadrant pick. `undefined` when the total weight is zero (nobody
 * owns a planet yet) — the caller skips the spawn that year.
 */
export function pickBossQuadrant(
  weights: { [quadrant: string]: number },
  random: () => number = Math.random,
): string | undefined {
  const entries = Object.entries(weights).filter(([, weight]) => weight > 0).sort(([a], [b]) => a.localeCompare(b))
  const total = entries.reduce((sum, [, weight]) => sum + weight, 0)
  if (total <= 0) {
    return undefined
  }
  let roll = random() * total
  for (const [quadrant, weight] of entries) {
    roll -= weight
    if (roll < 0) {
      return quadrant
    }
  }
  return entries[entries.length - 1][0]
}

/** Colors deliberately outside the player palette (`getPlayerColor`). */
export const BOSS_RACES: ReadonlyArray<{ name: string; color: string }> = [
  { name: 'Vorlag Armada', color: '#ff7a00' },
  { name: 'Krellith Swarm', color: '#e6c200' },
  { name: 'Xanthid Dominion', color: '#7b2fbe' },
  { name: 'Morvax Reavers', color: '#f2f2f2' },
  { name: 'Ashkari Host', color: '#9c1f4e' },
]
